import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { collection, query, where, orderBy } from "firebase/firestore";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Lock, Plus, Trash2, Upload, Users } from "lucide-react";
import { db } from "../../../lib/firebase";
import { useFirestoreCollectionOnce } from "../../../hooks/useFirestoreCollectionOnce";
import { useAllPersons } from "../../../hooks/useAllPersons";
import { useDeleteAudience } from "../../../hooks/useAudienceMutations";
import { useSnackbar } from "../../../context/SnackbarContext";
import { PageHeader } from "../../../components/PageHeader";
import { DataTable, type Column } from "../../../components/DataTable";
import { ConfirmDialog } from "../../../components/ConfirmDialog";
import { formatAudienceLabel, type Audience, type Role } from "@event-planner/shared";
import type { AudienceLabelUser } from "@event-planner/shared";
import { PhoneHeaderActions } from "../../../components/phone/PhoneHeaderActions";
import type { OverflowAction } from "../../../components/phone/OverflowMenu";
import { useIsPhone } from "../../../hooks/useIsPhone";
import { ExcelGroupImportDialog } from "./ExcelGroupImportDialog";

const ROLE_LABELS: Record<Role, string> = {
  admin: "Admins",
  organizer: "Organizers",
  internal: "Internal team",
  invitee: "Invitees",
  companion: "Companions",
};

/**
 * Groups page (ADR-120). Lists named audiences only:
 *   - Role audiences (`name != null && system == true`) → "Built-in groups"
 *   - Custom groups (`name != null && system == false`) → "Custom groups"
 * Anonymous compositions and personal audiences (both `name == null`) are
 * intentionally hidden — they're owned audiences scoped to a single
 * parent doc or user, not user-discoverable resources.
 */
export function GroupsListPage() {
  const isPhone = useIsPhone();
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useSnackbar();
  const { remove, loading: deleting } = useDeleteAudience();
  const [deleteTarget, setDeleteTarget] = useState<Audience | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  const audiencesQuery = useMemo(
    () =>
      eventId
        ? query(
            collection(db, "audiences"),
            where("eventId", "==", eventId),
            orderBy("name", "asc"),
          )
        : null,
    [eventId],
  );

  const {
    data: audiences,
    loading,
    refetch,
  } = useFirestoreCollectionOnce<Audience>(audiencesQuery);
  const { data: persons } = useAllPersons();

  // ADR-137: the audience cache is personId-only (`resolvedPersonIds[]`).
  // The active/pending breakdown is derived client-side by joining
  // against the loaded persons collection — claimed people are those
  // with a non-null `authUid` on the persons row.
  const claimedPersonIds = useMemo(
    () => new Set(persons.filter((p) => p.authUid != null).map((p) => p.id)),
    [persons],
  );
  function activeCountFor(audience: Audience): number {
    let n = 0;
    for (const personId of audience.resolvedPersonIds ?? []) {
      if (claimedPersonIds.has(personId)) n += 1;
    }
    return n;
  }
  function pendingCountFor(audience: Audience): number {
    return Math.max(
      0,
      (audience.resolvedPersonIds ?? []).length - activeCountFor(audience),
    );
  }

  const { systemGroups, customGroups } = useMemo(() => {
    const named = audiences.filter((a) => a.name !== null);
    const system = named
      .filter((a) => a.system)
      .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
    const custom = named
      .filter((a) => !a.system)
      .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
    return { systemGroups: system, customGroups: custom };
  }, [audiences]);

  const audienceById = useMemo(
    () => new Map(audiences.map((a) => [a.id, a])),
    [audiences],
  );
  const userById = useMemo<Map<string, AudienceLabelUser>>(
    () =>
      new Map(
        persons.map((p) => [p.id, { id: p.id, displayName: p.displayName }]),
      ),
    [persons],
  );

  function renderGroupSources(group: Audience) {
    const roleLabels = group.members.roles.map((role) => ROLE_LABELS[role]);
    const includedLabels = group.members.includedAudienceIds.map((id) =>
      formatAudienceLabel(audienceById.get(id), audienceById, userById),
    );
    const directLabels = group.members.personIds.map(
      (id: string) => userById.get(id)?.displayName ?? "Unknown person",
    );
    const labels = [...includedLabels, ...roleLabels, ...directLabels];
    if (labels.length === 0) {
      return <Typography variant="body2" color="text.secondary">No members configured</Typography>;
    }
    return (
      <Stack direction="row" gap={0.75} flexWrap="wrap">
        {labels.slice(0, 4).map((label, index) => (
          <Chip key={`${label}-${index}`} label={label} size="small" />
        ))}
        {labels.length > 4 && (
          <Chip label={`+${labels.length - 4} more`} size="small" variant="outlined" />
        )}
      </Stack>
    );
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      await remove(deleteTarget.id);
      showSuccess("Group deleted");
      setDeleteTarget(null);
      refetch();
    } catch {
      showError("Failed to delete group");
    }
  }

  const columns: Column<Audience>[] = [
    {
      id: "name",
      label: "Group",
      width: "42%",
      render: (g) => (
        <Stack spacing={0.5}>
          <Typography variant="body2" fontWeight={600}>{g.name}</Typography>
          {g.description && (
            <Typography variant="caption" color="text.secondary">
              {g.description}
            </Typography>
          )}
        </Stack>
      ),
    },
    {
      id: "resolved",
      label: "Members",
      render: (g) => {
        const active = activeCountFor(g);
        const pending = pendingCountFor(g);
        return (
          <Tooltip
            title={`${active} active, ${pending} pending sign-in`}
            arrow
          >
            <span data-testid={`group-member-count-${g.id}`}>
              {active + pending}
            </span>
          </Tooltip>
        );
      },
      align: "right",
      width: "96px",
      phone: "hidden",
    },
    {
      id: "sources",
      label: "Built From",
      phone: "chips",
      render: renderGroupSources,
    },
    {
      id: "actions",
      label: "",
      align: "right",
      render: (g) =>
        (
          <Tooltip title="Delete group">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteTarget(g);
              }}
              aria-label={`Delete ${g.name}`}
            >
              <Trash2 size={18} />
            </IconButton>
          </Tooltip>
        ),
      width: "72px",
      phone: "hidden",
    },
  ];

  // Phone: name and description, then "N members" in words, the
  // built-from chips, and delete in the row menu.
  const phoneColumns: Column<Audience>[] = [
    columns[0],
    {
      id: "members",
      label: "Members",
      phone: "secondary",
      render: (g) => {
        const active = activeCountFor(g);
        const pending = pendingCountFor(g);
        const total = active + pending;
        return (
          <span data-testid={`group-member-count-${g.id}`}>
            {total} member{total === 1 ? "" : "s"}
            {pending > 0 ? ` (${pending} pending sign-in)` : ""}
          </span>
        );
      },
    },
    ...columns.slice(1),
  ];
  function groupRowActions(g: Audience): OverflowAction[] {
    return [
      {
        label: "Delete group",
        icon: <Trash2 size={18} />,
        onClick: () => setDeleteTarget(g),
        destructive: true,
      },
    ];
  }

  return (
    <Box data-testid="groups-page">
      <PageHeader
        title="Groups"
        action={
          isPhone ? (
            <PhoneHeaderActions
              primary={
                <Button
                  variant="contained"
                  startIcon={<Plus size={18} />}
                  onClick={() => navigate("new")}
                  data-testid="new-group-button"
                >
                  New Group
                </Button>
              }
              more={[
                {
                  label: "Import from Excel",
                  icon: <Upload size={16} />,
                  onClick: () => setImportOpen(true),
                  testId: "import-groups-button",
                },
              ]}
            />
          ) : (
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<Upload size={16} />}
              onClick={() => setImportOpen(true)}
              data-testid="import-groups-button"
            >
              Import from Excel
            </Button>
            <Button
              variant="contained"
              startIcon={<Plus size={18} />}
              onClick={() => navigate("new")}
              data-testid="new-group-button"
            >
              New Group
            </Button>
          </Stack>
          )
        }
      />
      {!loading && systemGroups.length === 0 && customGroups.length === 0 ? (
        <Typography color="text.secondary">
          No groups yet. Built-in groups are created automatically with the
          event. Create custom groups for shuttles, activities, meal seating,
          or any attendee set organizers need to target.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {(loading || systemGroups.length > 0) && (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: "background.paper",
              }}
            >
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
                spacing={1.5}
                sx={{ mb: 1.5 }}
              >
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Built-in groups
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Auto-managed from event roles. Use them directly, or build
                    custom groups from them.
                  </Typography>
                </Box>
                <Tooltip title="Built-in groups are locked because membership follows event roles.">
                  <Chip
                    icon={<Lock size={14} />}
                    label="Auto-managed"
                    size="small"
                    variant="outlined"
                  />
                </Tooltip>
              </Stack>
              <Stack direction="row" gap={1} flexWrap="wrap">
                {systemGroups.map((group) => {
                  const active = activeCountFor(group);
                  const pending = pendingCountFor(group);
                  return (
                    <Tooltip
                      key={group.id}
                      title={`${active} active, ${pending} pending sign-in`}
                      arrow
                    >
                      <Chip
                        icon={<Users size={14} />}
                        label={`${group.name} (${active + pending})`}
                        variant="outlined"
                        data-testid={`role-audience-chip-${group.id}`}
                      />
                    </Tooltip>
                  );
                })}
              </Stack>
            </Paper>
          )}

          {!loading && (
            <Box>
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "flex-end" }}
                spacing={1}
                sx={{ mb: 1 }}
              >
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Custom groups
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Reusable groups for sessions, travel, and notifications.
                  </Typography>
                </Box>
              </Stack>
              {customGroups.length === 0 ? (
                <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                  <Typography color="text.secondary">
                    No custom groups yet. Create groups for shuttles,
                    activities, meal seating, or any attendee set organizers
                    need to target.
                  </Typography>
                </Paper>
              ) : (
                <DataTable
                  columns={isPhone ? phoneColumns : columns}
                  data={customGroups}
                  loading={false}
                  ariaLabel="Custom groups table"
                  getRowKey={(g) => g.id}
                  onRowClick={(g) => navigate(g.id)}
                  phoneRowActions={groupRowActions}
                  phoneRowActionsLabel={(g) => `More actions for ${g.name}`}
                />
              )}
            </Box>
          )}
        </Box>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Group"
        message={`Delete "${deleteTarget?.name}"? Any sessions or travel items targeting this group will lose it from their audience. This cannot be undone.`}
        loading={deleting}
      />

      {eventId && (
        <ExcelGroupImportDialog
          open={importOpen}
          onClose={() => setImportOpen(false)}
          eventId={eventId}
          onImported={refetch}
        />
      )}
    </Box>
  );
}
